import React, { useCallback, useEffect, useRef, useState } from "react"
import Nav from "../components/Nav"
import CosmosBackground from "../components/CosmosBackground"
import HeroBanner from "../components/HeroBanner"
import Row from "../components/Row"
import AppDownloadModal from "../components/AppDownloadModal"
import requests from "../api/requests"
import { useDispatch } from "react-redux"
import { useSnackbar } from "react-simple-snackbar"
import { errorSnackBar } from "../components/snackBarOptions"
import { useNavigate } from "react-router-dom"
import { Skeleton } from "@mui/material"
import { handleAPIError } from "../api/error"
import { isMobile, getOperatingSystem } from "../utils/deviceDetection"
import { useAppModalState } from "../utils/useAppModalState"

function extractSchedulePreview(html) {
  if (!html) return null
  const paragraphs = []
  const pRegex = /<p>([\s\S]*?)<\/p>/gi
  let match
  while ((match = pRegex.exec(html)) !== null) {
    const text = match[1].replace(/<[^>]+>/g, "").trim()
    if (text) paragraphs.push(text)
    if (paragraphs.length >= 2) break
  }
  if (paragraphs.length > 0) return paragraphs.join(" · ")
  return html.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim().slice(0, 200) || null
}

function HomeScreen() {
  const [sections, setSections] = useState([])
  const [bannerSlides, setBannerSlides] = useState([])
  const [scheduleViewedLocal, setScheduleViewedLocal] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [bannersLoading, setBannersLoading] = useState(true)
  const [isLoadingNextPage, setIsLoadingNextPage] = useState(false)
  const [userOS, setUserOS] = useState('')
  
  const { shouldShowModal, dismissModal } = useAppModalState()
  const [showAppModal, setShowAppModal] = useState(false)

  const observer = useRef()
  const sectionRef = useCallback(
    (node, index) => {
      sectionsRef.current[index] = node

      if (sections.length === index + 1) {
        if (loading || isLoadingNextPage) return
        if (observer.current) observer.current.disconnect()

        observer.current = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting && hasMore) {
            setPage((prev) => prev + 1)
          }
        })

        if (node) observer.current.observe(node)
      }
    },
    [sections, loading, hasMore, isLoadingNextPage],
  )

  const [openErrorSnackbar] = useSnackbar(errorSnackBar)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const sectionsRef = useRef([])

  useEffect(() => {
    requests
      .getHomeBanners()
      .then((res) => setBannerSlides(res.data || []))
      .catch(() => {})
      .finally(() => setBannersLoading(false))
  }, [])

  useEffect(() => {
    const checkMobileAndShowModal = () => {
      if (isMobile() && shouldShowModal) {
        const os = getOperatingSystem()
        if (os === 'ios' || os === 'android') {
          setUserOS(os)
          setTimeout(() => {
            setShowAppModal(true)
          }, 1500)
        }
      }
    }

    checkMobileAndShowModal()
  }, [shouldShowModal])

  const scrollToSection = (index) => {
    const headerOffset = 80
    const elementPosition =
      sectionsRef.current[index]?.getBoundingClientRect().top
    const offsetPosition = elementPosition + window.scrollY - headerOffset

    window.scrollTo({ top: offsetPosition, behavior: "smooth" })
  }

  useEffect(() => {
    async function fetchData() {
      try {
        if (page > 1) setIsLoadingNextPage(true)
        const req = await requests.getSections(page, 15)
        setSections((prev) => [...prev, ...req.data.sections])
        setHasMore(req.data.hasNextPage)

        return
      } catch (error) {
        handleAPIError(error, { dispatch, navigate, openErrorSnackbar }, true)
      } finally {
        setLoading(false)
        setIsLoadingNextPage(false)
      }

      return
    }

    fetchData()
    return
  }, [page, dispatch, navigate])

  const loadingRow = (
    <div>
      <Skeleton
        variant="text"
        height={"50px"}
        width={"300px"}
        animation={"wave"}
        style={{ margin: "20px 20px 0px 20px", borderRadius: "5px" }}
      />
      <div
        style={{
          margin: "20px 40px 0px 40px",
          display: "flex",
          overflowX: "hidden",
          overflowY: "hidden",
          whiteSpace: "nowrap",
          borderRadius: "5px"
        }}
      >
        {[...Array(10)].map((_, index) => (
          <Skeleton
            key={index}
            variant="rect"
            height={"375px"}
            width={"250px"}
            animation={"wave"}
            style={{
              minWidth: "250px",
              minHeight: "375px",
              display: "inline-block",
              marginRight: "10px",
              objectFit: "contain",
              borderRadius: "5px"
            }}
          />
        ))}
      </div>
    </div>
  )

  const loadingPage = (
    <div style={{ overflow: "hidden", position: "relative" }}>
      <CosmosBackground />
      <Nav solid />
      <div style={{ margin: "80px 16px 0px 16px" }}>
        <Skeleton
          variant="rect"
          height={"190px"}
          width={"100%"}
          animation={"wave"}
          style={{ borderRadius: "16px" }}
        />
      </div>
      {loadingRow}
    </div>
  )

  return (!loading && !bannersLoading) ? (
    <div
      style={{
        width: "100%",
        overflowX: "hidden",
        position: "relative",
      }}
    >
      <CosmosBackground />
      <Nav
        sections={sections}
        scrollFunc={scrollToSection}
        hideSumary={false}
        solid
      />
      <HeroBanner
        slides={bannerSlides.map((slide) => {
          if (slide.type === "schedule") {
            const isNew = slide.is_new && !scheduleViewedLocal
            return {
              id: "schedule",
              badge: "Programação",
              title: slide.title,
              description: slide.body_html || null,
              cta: slide.cta_text || "Ver programação",
              isNew,
              onClick: () => {
                if (isNew) {
                  requests
                    .markScheduleViewed({ reference_month: slide.reference_month })
                    .catch(() => {})
                  setScheduleViewedLocal(true)
                }
                navigate("/schedule")
              },
            }
          }
          if (slide.type === "highlight") {
            return {
              id: "highlight",
              badge: "Destaque",
              title: slide.title,
              description: slide.description || null,
              cta: slide.cta_text || "Assistir",
              poster: slide.thumbnail || null,
              onClick: () => navigate(`/${slide.content_type}/${slide.content_id}`),
            }
          }
          if (slide.type === "custom") {
            return {
              id: `custom-${slide.banner_id}`,
              badge: "Novidades",
              title: slide.title,
              description: slide.description || null,
              cta: slide.cta_text || "Ver mais",
              onClick: () => {
                const link = slide.cta_link || "/"
                if (link.startsWith("http")) {
                  window.open(link, "_blank", "noopener,noreferrer")
                } else {
                  navigate(link)
                }
              },
            }
          }
          return null
        }).filter(Boolean)}
      />
      {sections.map((section, i) => {
        if (!section?.contents || section?.contents?.length === 0) return null
        const isContinueWatching = section.contents.some(
          (c) => (c?.watched_percentage || 0) > 0
        )
        return (
          <Row
            key={i}
            index={section?._id}
            refFunc={(el) => sectionRef(el, i)}
            title={section?.title}
            contents={section?.contents}
            featured={isContinueWatching}
          />
        )
      })}
      {isLoadingNextPage && loadingRow}
      
      {/* Modal do App para dispositivos móveis */}
      <AppDownloadModal
        open={showAppModal}
        onClose={() => {
          setShowAppModal(false)
          dismissModal()
        }}
        operatingSystem={userOS}
      />
    </div>
  ) : (
    loadingPage
  )
}

export default HomeScreen
